/* ============================================================
   CalendarView — date 속성 기준 월간 캘린더 뷰
   - 2026-07-06 DatabasePage.jsx 에서 그대로 추출(동작 동일) —
     DatabaseEmbed(링크된 DB 인라인 뷰)와 공유하기 위한 분리.
   ============================================================ */
import { useMemo, useState } from 'react';
import { optClass } from './viewPipeline';

export default function CalendarView({ columns, rows, view, onChangeDateKey }) {
  const dateCols = columns.filter((c) => c.type === 'date');
  const dateKey = view.config?.dateKey || columns.find((c) => c.type === 'date')?.key;
  const dateCol = columns.find((c) => c.key === dateKey);
  const statusCol = columns.find((c) => c.type === 'select');
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m: d.getMonth() }; // m: 0-11
  });

  const today = new Date();
  const firstDay = new Date(cursor.y, cursor.m, 1);
  const startDow = firstDay.getDay(); // 0=일
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const daysInPrev = new Date(cursor.y, cursor.m, 0).getDate();

  // 날짜별 행 인덱스
  const byDate = useMemo(() => {
    const map = {};
    if (!dateKey) return map;
    rows.forEach((r) => {
      const v = r.properties?.[dateKey];
      if (!v) return;
      const key = String(v).slice(0, 10); // YYYY-MM-DD
      (map[key] = map[key] || []).push(r);
    });
    return map;
  }, [rows, dateKey]);

  // 6주 그리드 셀 구성
  const cells = [];
  for (let i = 0; i < startDow; i++) {
    cells.push({ day: daysInPrev - startDow + 1 + i, dim: true, key: `p${i}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dim: false, iso, key: `c${d}` });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ day: cells.length % 7, dim: true, key: `n${cells.length}` });
  }

  const monthLabel = `${cursor.y}년 ${cursor.m + 1}월`;
  const prev = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const next = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  if (!dateCol) {
    return (
      <div className="db-state">
        <div className="big">📅</div>
        <div>캘린더에 사용할 날짜(date) 컬럼을 만들어 주세요.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="calhead">
        <button className="t" onClick={prev}>◀</button>
        <b>{monthLabel}</b>
        <button className="t" onClick={next}>▶</button>
        <button className="today" onClick={goToday}>오늘</button>
        {dateCols.length > 1 && (
          <span className="grp-bar inline">
            <span className="grp-label">날짜 기준</span>
            <select
              className="grp-select"
              value={dateKey || ''}
              onChange={(e) => onChangeDateKey(e.target.value)}
            >
              {dateCols.map((c) => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          </span>
        )}
      </div>
      <div className="cal">
        <div className="calg">
          {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
            <div className="cdow" key={d}>{d}</div>
          ))}
        </div>
        <div className="calg">
          {cells.map((cell) => {
            const events = !cell.dim && cell.iso ? byDate[cell.iso] || [] : [];
            const isToday =
              !cell.dim &&
              today.getFullYear() === cursor.y &&
              today.getMonth() === cursor.m &&
              today.getDate() === cell.day;
            return (
              <div className={`ccell${cell.dim ? ' dim' : ''}${isToday ? ' istoday' : ''}`} key={cell.key}>
                <div className="d">{cell.day}</div>
                {events.map((r) => {
                  const sv = statusCol ? r.properties?.[statusCol.key] : '';
                  const cls = sv ? optClass(statusCol, sv) : 'tbl';
                  return (
                    <div className={`ev ${cls}`} key={r.page_id} title={r.title}>
                      {r.title || '제목 없음'}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
